import { useState, useEffect, useRef, useCallback } from 'react';
import { playUserAudio } from '../../../utils/dialogUtils';
import { handleError } from '../../../context/errors/errorUtils';

/*
block,              // Объект с данными урока (слова, примеры, аудио)
mode,               // Режим игры: 'sequential' или 'parallel'
currentSentenceIndex, // Индекс текущей фразы (для sequential режима)
setCurrentSentenceIndex, // Функция для обновления currentSentenceIndex
onComplete,         // Callback, вызываемый при завершении раунда
previousScore,      // Начальный счёт (для добавления к текущему)
/**/
// подробное описание в useSentenceGameLogicInstruction.txt


export const useSentenceGameLogic = ({ block, mode, currentSentenceIndex, setCurrentSentenceIndex, onComplete, previousScore, actions, combinedIsAndroidApp, site }) => {
  //const maxWords = 30;
  const maxParallelSentences = 3;
  const sentencesPerRound = 10;

  // Состояния
  const [slots, setSlots] = useState({});
  const [availableWords, setAvailableWords] = useState([]);
  const [wordStyles, setWordStyles] = useState({});
  const [showCorrect, setShowCorrect] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [fadingSentences, setFadingSentences] = useState({});
  const [score, setScore] = useState(0);
  const [usedSentenceIndices, setUsedSentenceIndices] = useState([]);
  const [activeSentences, setActiveSentences] = useState([]);
  const [completedSentences, setCompletedSentences] = useState(0);
  const [draggedWord, setDraggedWord] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [selectedSentences, setSelectedSentences] = useState([]);
  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);

  // Рефы
  const slotsRef = useRef({});
  const wordsRef = useRef([]);
  const containerRef = useRef(null);
  const dragPositionRef = useRef({ x: 0, y: 0 });
  const draggedWordRef = useRef(null);
  const hoveredSlotRef = useRef(null);

  // Формируем все фразы (для всех слов)
  const allSentences = block.words.flatMap((word, wordIndex) =>
    word.examples.map((example, exampleIndex) => {
      const globalIndex = `${wordIndex}-${exampleIndex}`;
      return {
        phrase: example.phrase,
        translation: example.translation,
        audio: example.audio,
        words: example.translation
          .split(' ')
          .map((word, wordIndexInTranslation) => ({
            id: `${globalIndex}-${wordIndexInTranslation}-${word}`,
            value: word,
          })),
        index: globalIndex,
      };
    })
  );

  // Получение предложений для раунда
  const getSentencesForRound = (reset = false) => {
    let newUsedIndices = reset ? [] : [...usedSentenceIndices];
    let selectedSentences = [];

    const availableSentences = allSentences.filter(
      (s) => !newUsedIndices.includes(s.index)
    );

    while (selectedSentences.length < sentencesPerRound && availableSentences.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableSentences.length);
      const selectedSentence = availableSentences.splice(randomIndex, 1)[0];
      selectedSentences.push(selectedSentence);
      newUsedIndices.push(selectedSentence.index);
    }

    setUsedSentenceIndices(newUsedIndices);
    return selectedSentences.slice(0, sentencesPerRound);
  };

  // Инициализация предложений
  useEffect(() => {
    if (mode === 'sequential') {
      if (selectedSentences.length === 0) {
        const shuffledSentences = [...allSentences].sort(() => Math.random() - 0.5);
        const newSelectedSentences = shuffledSentences.slice(0, Math.min(10, allSentences.length));
        setSelectedSentences(newSelectedSentences);
      }

      const sentence = selectedSentences[currentSentenceIndex] || allSentences[0];
      if (!sentence) {
        console.warn('No sentence found for currentSentenceIndex:', currentSentenceIndex);
        return;
      }

      const shuffled = [...sentence.words].sort(() => Math.random() - 0.5);
      const newWordStyles = shuffled.reduce((acc, word) => {
        acc[word.id] = getRandomStyle();
        return acc;
      }, {});
      setSlots({ [currentSentenceIndex]: sentence.words.map(() => null) });
      setAvailableWords(shuffled);
      setWordStyles(newWordStyles);
      setShowCorrect({ [currentSentenceIndex]: false });
      setErrorMessages({ [currentSentenceIndex]: '' });
      setFadingSentences({ [currentSentenceIndex]: false });
    } else {
      const roundSentences = getSentencesForRound();
      setActiveSentences(roundSentences.slice(0, maxParallelSentences));
      const newSlots = {};
      const newShowCorrect = {};
      const newErrorMessages = {};
      const newFadingSentences = {};
      const allWords = roundSentences
        .slice(0, maxParallelSentences)
        .flatMap((sentence) => sentence.words);
      const uniqueWords = Array.from(
        new Map(allWords.map((word) => [word.id, word])).values()
      );
      const shuffledWords = uniqueWords.sort(() => Math.random() - 0.5);
      const newWordStyles = shuffledWords.reduce((acc, word) => {
        acc[word.id] = getRandomStyle();
        return acc;
      }, {});
      roundSentences.slice(0, maxParallelSentences).forEach((sentence) => {
        newSlots[sentence.index] = sentence.words.map(() => null);
        newShowCorrect[sentence.index] = false;
        newErrorMessages[sentence.index] = '';
        newFadingSentences[sentence.index] = false;
      });
      setSlots(newSlots);
      setAvailableWords(shuffledWords);
      setWordStyles(newWordStyles);
      setShowCorrect(newShowCorrect);
      setErrorMessages(newErrorMessages);
      setFadingSentences(newFadingSentences);
      setCompletedSentences(0);
    }
  }, [currentSentenceIndex, mode, selectedSentences]);

    // использование подсказки 
	const hintWord = (sentenceIndex) => {
		let sentence;
		if (mode === 'sequential') {
		  sentence = selectedSentences[sentenceIndex] || allSentences[0];
		} else {
		  sentence = allSentences.find((s) => s && s.index === sentenceIndex);
		}

	  if (!sentence) {
		handleError({
		  code: 'SENTENCE_NOT_FOUND_FOR_INDEX',
		  error,
		  context: {
			module: 'useSentenceGameLogic',
			message: 'Sentence not found for index',
			userId,
		  },
		  showToUser: true,
		});  
		return;
	  }

	  const currentSlots = slots[sentenceIndex];
	  if (!currentSlots) {
		handleError({
		  code: 'SLOTS_NOT_INITIALIZED_FOR_SENTENCEINDEX',
		  error,
		  context: {
			module: 'useSentenceGameLogic',
			message: 'Slots not initialized for sentenceIndex',
			userId,
		  },
		  showToUser: true,
		});  		  
		return;
	  }

	  // Найдём индексы незаполненных слотов
	  const emptySlotIndices = currentSlots
		.map((slot, index) => (slot === null ? index : null))
		.filter((i) => i !== null);

	  if (emptySlotIndices.length === 0) return;

	  // Выберем случайный пустой слот
	  const randomSlotIndex = emptySlotIndices[Math.floor(Math.random() * emptySlotIndices.length)];

	  // Определим нужное слово
	  const correctWord = sentence.words[randomSlotIndex];

	  // Найдём его в доступных словах
	  const wordInPool = availableWords.find((w) => w.id === correctWord.id);
	  if (!wordInPool) {
		handleError({
		  code: 'CORRECT_WORD_NOT_FOUND_IN_AVAILABLEWORDS',
		  error,
		  context: {
			module: 'useSentenceGameLogic',
			message: 'Correct word not found in availableWords',
			userId,
		  },
		  showToUser: true,
		});  		  
		return;
	  }

	  // Обновим слоты и доступные слова
	  const newSlots = { ...slots };
	  newSlots[sentenceIndex] = [...currentSlots];
	  newSlots[sentenceIndex][randomSlotIndex] = wordInPool;

	  const newAvailableWords = availableWords.filter((w) => w.id !== correctWord.id);

	  setSlots(newSlots);
	  setAvailableWords(newAvailableWords);

	  // Автоматически проверим, если всё заполнено
	  if (newSlots[sentenceIndex].every((word) => word !== null)) {
		checkAnswer(sentenceIndex, newSlots[sentenceIndex]);
	  }
	};

  // Улучшенная функция checkAnswer
  const checkAnswer = (sentenceIndex, currentSlots) => {
    let sentence;
    let targetIndex = sentenceIndex;
    
    if (mode === 'sequential') {
      sentence = selectedSentences[currentSentenceIndex];
      targetIndex = currentSentenceIndex;
    } else {
      // В параллельном режиме сначала проверяем среди активных предложений
      sentence = activeSentences.find((s) => s && s.index === sentenceIndex);
      
      // Если не найдено среди активных, ищем во всех предложениях
      if (!sentence) {
        sentence = allSentences.find((s) => s && s.index === sentenceIndex);
      }
      
      targetIndex = sentenceIndex;
    }

    if (!sentence || !sentence.words) {
		handleError({
		  code: 'SENTENCE_NOT_FOUND_FOR_CHECKANSWER',
		  error,
		  context: {
			module: 'useSentenceGameLogic',
			message: 'Sentence not found for checkAnswer',
			userId,
		  },
		  showToUser: true,
		});  		
      return;
    }

    const isCorrect = currentSlots.every(
      (word, i) => word && word.value.toLowerCase() === sentence.words[i].value.toLowerCase()
    );

    if (isCorrect) {
      setScore((prev) => prev + currentSlots.length);
      setShowCorrect((prev) => ({ ...prev, [targetIndex]: true }));
      setFadingSentences((prev) => ({ ...prev, [targetIndex]: true }));
      setIsDraggingDisabled(true);

      // Проигрывание аудио
      if (sentence.audio) {
	    const audioSrc = `${combinedIsAndroidApp ? site : ''}${sentence.audio}`;  
        playUserAudio(audioSrc);  
      }

      setTimeout(() => {
        setIsDraggingDisabled(false);
        if (mode === 'sequential') {
          moveToNextSentence();
        } else {
          setCompletedSentences((prev) => prev + 1);
          
          // Обновляем активные предложения, удаляя текущее
          setActiveSentences((prev) => {
            const newActive = prev.filter((s) => s && s.index !== sentenceIndex);
            
            // Получаем следующее предложение, если есть
            const remainingSentences = allSentences
              .filter(
                (s) => s && 
                usedSentenceIndices.includes(s.index) &&
                !newActive.some((as) => as && as.index === s.index) &&
                s.index !== sentenceIndex
              )
              .sort(() => Math.random() - 0.5)
              .slice(0, 1);
              
            // Объединяем текущие активные с новыми
            const updatedActive = [...newActive, ...remainingSentences];
            
            // Обновляем слоты и связанные состояния
            const newSlots = { ...slots };
            const newShowCorrect = { ...showCorrect };
            const newErrorMessages = { ...errorMessages };
            const newFadingSentences = { ...fadingSentences };

            // Удаляем слоты для текущего предложения
            delete newSlots[sentenceIndex];
            delete newShowCorrect[sentenceIndex];
            delete newErrorMessages[sentenceIndex];
            delete newFadingSentences[sentenceIndex];
            
            // Подготавливаем слоты для новых предложений
            remainingSentences.forEach((sentence) => {
              if (sentence && sentence.index && sentence.words) {
                newSlots[sentence.index] = sentence.words.map(() => null);
                newShowCorrect[sentence.index] = false;
                newErrorMessages[sentence.index] = '';
                newFadingSentences[sentence.index] = false;
              }
            });
            
            // Собираем слова для доступных предложений
            const allActiveWords = updatedActive
              .filter(s => s && s.words)
              .flatMap(s => s.words || []);
              
            const newSentenceWords = remainingSentences
              .filter(s => s && s.words)
              .flatMap(s => s.words || []);
              
            // Находим уже использованные слова
            const usedWordIds = Object.entries(newSlots)
              .flatMap(([, slots]) => slots)
              .filter(word => word)
              .map(word => word.id);
              
            // Формируем уникальные слова для доступа
            const uniqueNewWords = Array.from(
              new Map(
                [
                  ...allActiveWords.filter(word => !usedWordIds.includes(word.id)),
                  ...newSentenceWords,
                ].map(word => [word.id, word])
              ).values()
            );
            
            // Перемешиваем слова
            const shuffledNewWords = uniqueNewWords.sort(() => Math.random() - 0.5);
            
            // Обновляем стили для слов
            const newWordStyles = { ...wordStyles };
            allActiveWords.forEach(word => {
              if (!newWordStyles[word.id]) {
                newWordStyles[word.id] = getRandomStyle();
              }
            });
            
            // Обновляем состояния
            setSlots(newSlots);
            setAvailableWords(shuffledNewWords);
            setWordStyles(newWordStyles);
            setShowCorrect(newShowCorrect);
            setErrorMessages(newErrorMessages);
            setFadingSentences(newFadingSentences);
            
            // Возвращаем обновленные активные предложения
            return updatedActive.slice(0, maxParallelSentences);
          });
          
          // Обновляем использованные индексы
          setUsedSentenceIndices(prev => prev.filter(index => index !== sentenceIndex));
        }
      }, 2000);
    } else if (currentSlots.every((word) => word !== null)) {
      const userPhrase = currentSlots.map((word) => word.value).join(' ');
      const correctPhrase = sentence.words.map((word) => word.value).join(' ');
      actions.showError();
      setErrorMessages((prev) => ({
        ...prev,
        [targetIndex]: '❌, try again!',
      }));
      setTimeout(() => {
        resetSentence(targetIndex);
      }, 1000);
    }
  };

  // Сброс предложения
  const resetSentence = (sentenceIndex) => {
    let sentence;
    if (mode === 'sequential') {
      sentence = selectedSentences[sentenceIndex] || allSentences[0];
    } else {
      sentence = allSentences.find((s) => s && s.index === sentenceIndex);
    }

    if (!sentence || !sentence.words) {
      return;
    }

    const newSlots = { ...slots };
    if (!newSlots[sentenceIndex]) {
		handleError({
		  code: 'SLOTS_NOT_FOUND_FOR_SENTENCEINDEX',
		  error,
		  context: {
			module: 'useSentenceGameLogic',
			message: 'Slots not found for sentenceIndex',
			userId,
		  },
		  showToUser: true,
		});  	  	
      return;
    }

    const resetWords = newSlots[sentenceIndex].filter((word) => word);
    const newWordStyles = { ...wordStyles };
    resetWords.forEach((word) => {
      if (!newWordStyles[word.id]) {
        newWordStyles[word.id] = getRandomStyle();
      }
    });
    newSlots[sentenceIndex] = sentence.words.map(() => null);
    setSlots(newSlots);

    const activeWords = (mode === 'sequential' ? [sentence] : activeSentences)
      .filter((sent) => sent && sent.words && sent.index)
      .flatMap((sent) => sent.words.map((word) => ({ ...word, sentenceIndex: sent.index })))
      .filter((w) => {
        const sentenceSlots = slots[w.sentenceIndex];
        return !sentenceSlots?.some((slot) => slot?.id === w.id);
      });
    const uniqueWords = Array.from(
      new Map([...activeWords, ...resetWords].map((word) => [word.id, word])).values()
    );
    setAvailableWords(uniqueWords.sort(() => Math.random() - 0.5));
    setWordStyles(newWordStyles);
    setErrorMessages((prev) => ({ ...prev, [sentenceIndex]: '' }));
  };

  // Переход к следующему предложению
  const moveToNextSentence = () => {
    if (currentSentenceIndex < selectedSentences.length - 1) {
      setCurrentSentenceIndex(currentSentenceIndex + 1);
    } else {
      onComplete(score);
    }
  };

  // Играть заново
  const playMore = () => {
    const roundSentences = getSentencesForRound(true);
    setActiveSentences(roundSentences.slice(0, maxParallelSentences));
    setScore(0);
    setCompletedSentences(0);
    const newSlots = {};
    const newShowCorrect = {};
    const newErrorMessages = {};
    const newFadingSentences = {};
    const allWords = roundSentences
      .slice(0, maxParallelSentences)
      .flatMap((sentence) => sentence.words);
    const uniqueWords = Array.from(
      new Map(allWords.map((word) => [word.id, word])).values()
    );
    const shuffledWords = uniqueWords.sort(() => Math.random() - 0.5);
    const newWordStyles = shuffledWords.reduce((acc, word) => {
      acc[word.id] = getRandomStyle();
      return acc;
    }, {});
    roundSentences.slice(0, maxParallelSentences).forEach((sentence) => {
      newSlots[sentence.index] = sentence.words.map(() => null);
      newShowCorrect[sentence.index] = false;
      newErrorMessages[sentence.index] = '';
      newFadingSentences[sentence.index] = false;
    });
    setSlots(newSlots);
    setAvailableWords(shuffledWords);
    setWordStyles(newWordStyles);
    setShowCorrect(newShowCorrect);
    setErrorMessages(newErrorMessages);
    setFadingSentences(newFadingSentences);
  };

  // Генерация случайного стиля
  const getRandomStyle = () => {
    const angle = Math.random() * 30 - 15;
    const offsetX = Math.random() * 20 - 10;
    const offsetY = Math.random() * 20 - 10;
    return {
      transform: `rotate(${angle}deg)`,
      left: `${offsetX}px`,
      top: `${offsetY}px`,
    };
  };

  // Обработчики перетаскивания
  const handleDragStart = (e, word) => {
    if (isDraggingDisabled) return;
    draggedWordRef.current = word;
    setDraggedWord(word);
    e.dataTransfer.setData('text/plain', word.id);
  };

  const handleDragOver = (e, sentenceIndex, slotIndex) => {
    if (isDraggingDisabled) return;
    e.preventDefault();
    hoveredSlotRef.current = { sentenceIndex, slotIndex };
    setHoveredSlot({ sentenceIndex, slotIndex });
  };

  const handleDragLeave = () => {
    if (isDraggingDisabled) return;
    hoveredSlotRef.current = null;
    setHoveredSlot(null);
  };

  const handleDrop = (e, targetSentenceIndex, targetSlotIndex) => {
    if (isDraggingDisabled) return;
    e.preventDefault();
    const wordId = e.dataTransfer.getData('text/plain');
    const word = availableWords.find((w) => w.id === wordId);

    if (!word || !availableWords.includes(word)) {
      setDraggedWord(null);
      setHoveredSlot(null);
      return;
    }

    const newSlots = { ...slots };
    if (!newSlots[targetSentenceIndex] || !Array.isArray(newSlots[targetSentenceIndex])) {
      const sentence = mode === 'sequential'
        ? selectedSentences[currentSentenceIndex]
        : allSentences.find((s) => s.index === targetSentenceIndex);
      if (sentence) {
        newSlots[targetSentenceIndex] = sentence.words.map(() => null);
      } else {
        return;
      }
    }

    newSlots[targetSentenceIndex][targetSlotIndex] = word;
    const newAvailableWords = availableWords.filter((w) => w.id !== word.id);

    setSlots(newSlots);
    setAvailableWords(newAvailableWords);
    setDraggedWord(null);
    setHoveredSlot(null);

    if (newSlots[targetSentenceIndex].every((word) => word !== null)) {
      checkAnswer(targetSentenceIndex, newSlots[targetSentenceIndex]);
    }
  };

  const handleSlotClick = (sentenceIndex, slotIndex) => {
    if (isDraggingDisabled) return;
    const word = slots[sentenceIndex][slotIndex];
    if (!word) return;

    const newSlots = { ...slots };
    newSlots[sentenceIndex][slotIndex] = null;
    setSlots(newSlots);
    setAvailableWords([...availableWords, word]);
  };

  const handleTouchStart = (e, word) => {
    if (isDraggingDisabled) return;
    draggedWordRef.current = word;
    setDraggedWord(word);
    const touch = e.touches[0];
    dragPositionRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (isDraggingDisabled || !draggedWordRef.current) return;
    
    if (hoveredSlot) {
      const word = draggedWordRef.current;
      const { sentenceIndex, slotIndex } = hoveredSlot;
  
      // Проверяем, существует ли предложение, прежде чем работать с ним
      let sentence;
      if (mode === 'sequential') {
        sentence = selectedSentences[currentSentenceIndex];
      } else {
        // В параллельном режиме ищем предложение среди активных предложений
        sentence = activeSentences.find(s => s && s.index === sentenceIndex);
      }
  
      if (!sentence) {
        console.warn('Sentence not found for sentenceIndex:', sentenceIndex);
        setDraggedWord(null);
        setHoveredSlot(null);
        draggedWordRef.current = null;
        hoveredSlotRef.current = null;
        return;
      }
  
      const newSlots = { ...slots };
      
      // Проверка существования массива слотов
      if (!newSlots[sentenceIndex] || !Array.isArray(newSlots[sentenceIndex])) {
        if (sentence.words && Array.isArray(sentence.words)) {
          newSlots[sentenceIndex] = sentence.words.map(() => null);
        } else {
			handleError({
			  code: 'INVALID_SENTENCE_STRUCTURE_FOR_SENTENCE',
			  error,
			  context: {
				module: 'useSentenceGameLogic',
				message: 'Invalid sentence structure for sentence',
				userId,
			  },
			  showToUser: true,
			});  			
          setDraggedWord(null);
          setHoveredSlot(null);
          draggedWordRef.current = null;
          hoveredSlotRef.current = null;
          return;
        }
      }
  
      // Проверка валидности индекса слота
      if (slotIndex < 0 || slotIndex >= newSlots[sentenceIndex].length) {
        setDraggedWord(null);
        setHoveredSlot(null);
        draggedWordRef.current = null;
        hoveredSlotRef.current = null;
        return;
      }
  
      newSlots[sentenceIndex][slotIndex] = word;
      const newAvailableWords = availableWords.filter((w) => w.id !== word.id);
  
      setSlots(newSlots);
      setAvailableWords(newAvailableWords);
  
      if (newSlots[sentenceIndex].every((word) => word !== null)) {
        checkAnswer(sentenceIndex, newSlots[sentenceIndex]);
      }
    }
  
    setDraggedWord(null);
    setHoveredSlot(null);
    draggedWordRef.current = null;
    hoveredSlotRef.current = null;
  };

  const handleDrag = (e) => {
    if (isDraggingDisabled) return;
    dragPositionRef.current = { x: e.clientX, y: e.clientY };
  };


// Удаляем объявление handleTouchMove из основного кода и Заменяем оба useEffect на один комбинированный
useEffect(() => {
  const container = containerRef.current;
  if (container) {
    // Обработчик touchMove для всех режимов
    const handleTouchMove = (e) => {
      if (isDraggingDisabled) return;
      
      // Проверяем, есть ли перетаскиваемое слово
      if (draggedWordRef.current) {
        e.preventDefault();
      }
      
      const touch = e.touches[0];
      dragPositionRef.current = { x: touch.clientX, y: touch.clientY };
      
      // Обновляем состояние draggedWord из ref
      setDraggedWord(draggedWordRef.current);
      
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const slot = Object.values(slotsRef.current)
        .flat()
        .find((ref) => ref && ref.contains(element));
        
      if (slot && slot.dataset) {
        const { sentenceIndex, slotIndex } = slot.dataset;
        
        // Проверка, существует ли предложение для данного sentenceIndex
        let sentence;
        if (mode === 'sequential') {
          sentence = selectedSentences[currentSentenceIndex];
        } else {
          // В параллельном режиме ищем среди активных предложений
          sentence = activeSentences.find(s => s && s.index === sentenceIndex);
        }
        
        if (!sentence) {
          console.warn('Sentence not found in touchMove for sentenceIndex:', sentenceIndex);
          return;
        }
        
        const newHoveredSlot = { 
          sentenceIndex: sentenceIndex, 
          slotIndex: parseInt(slotIndex) 
        };
        
        hoveredSlotRef.current = newHoveredSlot;
        setHoveredSlot(newHoveredSlot);
      } else if (hoveredSlotRef.current) {
        hoveredSlotRef.current = null;
        setHoveredSlot(null);
      }
    };
    
    // Устанавливаем touchAction напрямую в зависимости от наличия draggedWordRef.current
    container.style.touchAction = draggedWordRef.current ? 'none' : 'auto';
    
    // Добавляем слушатель
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }
}, [
  isDraggingDisabled, 
  setDraggedWord, 
  setHoveredSlot, 
  mode, 
  currentSentenceIndex, 
  selectedSentences, 
  activeSentences, 
  draggedWordRef.current
]);

  return {
    sentences: mode === 'sequential' ? selectedSentences : activeSentences,
    activeSentences,
    completedSentences, 
    sentencesPerRound, 
    slots,
    availableWords,
    wordStyles,
    showCorrect,
    errorMessages,
    fadingSentences,
    score,
    draggedWord,
    hoveredSlot,
    slotsRef,
    wordsRef,
    containerRef,
    dragPositionRef,
    isDraggingDisabled,
    checkAnswer,
    moveToNextSentence,
    resetSentence,
	hintWord,
    playMore,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleSlotClick,
    handleTouchStart,
    handleTouchEnd,
    handleDrag,
  };
};
